import { GridCellKind, getMiddleCenterBias, roundedRect } from '@glideapps/glide-data-grid';
import type { CustomCell, CustomRenderer, GridCell } from '@glideapps/glide-data-grid';
import { paletteValue } from '../../palette.js';

/** A button drawn in a cell: what it says, and how loudly. */
export interface GridAction {
  readonly id: string;
  readonly label: string;
  readonly tone?: 'primary' | 'danger' | undefined;
  readonly disabled?: boolean | undefined;
}

interface ActionsCellData {
  readonly kind: 'actions';
  readonly actions: readonly GridAction[];
}

export type ActionsCell = CustomCell<ActionsCellData>;

/**
 * The font is fixed rather than the theme's, because the click that lands on a
 * button is resolved outside a draw, where there is no theme to ask: the two
 * must measure the same words the same way.
 */
const FONT = '500 12px system-ui, -apple-system, sans-serif';
const INSET = 8;
const PADDING = 8;
const GAP = 6;
const HEIGHT = 22;
const RADIUS = 6;

let measurer: CanvasRenderingContext2D | null | undefined;

function widthOf(label: string): number {
  measurer ??= document.createElement('canvas').getContext('2d');
  if (measurer === null) return label.length * 7;
  measurer.font = FONT;
  return measurer.measureText(label).width;
}

/** Where each button sits, measured from the cell's left edge. */
function layout(
  actions: readonly GridAction[],
): { action: GridAction; x: number; width: number }[] {
  let x = INSET;
  return actions.map((action) => {
    const width = widthOf(action.label) + PADDING * 2;
    const placed = { action, x, width };
    x += width + GAP;
    return placed;
  });
}

/**
 * A row's commands as buttons, left to right. The same commands are on the
 * row's right-click menu, which is where the keyboard reaches them.
 */
export const actionsRenderer: CustomRenderer<ActionsCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is ActionsCell =>
    'kind' in cell.data && cell.data.kind === 'actions',
  needsHover: true,
  needsHoverPosition: true,
  draw: (args, cell) => {
    const { ctx, rect, theme, hoverX } = args;
    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.width, rect.height);
    ctx.clip();
    ctx.font = FONT;
    const bias = getMiddleCenterBias(ctx, FONT);
    const middle = rect.y + rect.height / 2;
    let pointer = false;

    for (const { action, x, width } of layout(cell.data.actions)) {
      const left = rect.x + x;
      const hovered =
        action.disabled !== true && hoverX !== undefined && hoverX >= x && hoverX <= x + width;
      if (hovered) pointer = true;
      const primary = action.tone === 'primary';
      ctx.globalAlpha = action.disabled === true ? 0.45 : 1;
      ctx.fillStyle = primary
        ? paletteValue('--accent')
        : paletteValue(hovered ? '--hover' : '--surface');
      ctx.beginPath();
      roundedRect(ctx, left + 0.5, middle - HEIGHT / 2 + 0.5, width - 1, HEIGHT - 1, RADIUS);
      ctx.fill();
      if (!primary) {
        ctx.strokeStyle = paletteValue('--line-strong');
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.fillStyle = primary
        ? paletteValue('--surface')
        : action.tone === 'danger'
          ? paletteValue('--danger')
          : theme.textDark;
      ctx.fillText(action.label, left + PADDING, middle + bias);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
    args.overrideCursor?.(pointer ? 'pointer' : undefined);
  },
};

export function actionsCell(actions: readonly GridAction[]): ActionsCell {
  return {
    kind: GridCellKind.Custom,
    allowOverlay: false,
    readonly: true,
    copyData: '',
    data: { kind: 'actions', actions },
  };
}

/** The button under a click this far from the cell's left edge, if there is one. */
export function actionAt(cell: GridCell, localX: number): GridAction | null {
  if (cell.kind !== GridCellKind.Custom) return null;
  const data: unknown = cell.data;
  if (
    typeof data !== 'object' ||
    data === null ||
    !('kind' in data) ||
    data.kind !== 'actions' ||
    !('actions' in data) ||
    !Array.isArray(data.actions)
  ) {
    return null;
  }
  const actions: readonly GridAction[] = data.actions;
  const hit = layout(actions).find(({ x, width }) => localX >= x && localX <= x + width);
  return hit === undefined || hit.action.disabled === true ? null : hit.action;
}
